import type { Messages } from '@/types'

const messages: Messages = {
    common: {
        actions: {
            add: 'Add',
            edit: 'Edit',
            delete: 'Delete',
            cancel: 'Cancel',
            back: 'Back',
            save: 'Save',
            update: 'Update',
            reset: 'Reset',
            refresh: 'Refresh',
            backToList: '@:common.back to list',
            resetDefaults: '@:common.reset to Defaults',
            confirm: 'Confirm',
            proceed: 'Proceed',
            close: 'Close',
            apply: 'Apply',
            search: 'Search',
            filter: 'Filter',
            clear: 'Clear',
            select: 'Select',
            upload: 'Upload',
            download: 'Download',
            view: 'View',
            sort: 'Sort',
            seeMore: 'See more'
        },
        status: {
            success: 'Success',
            error: 'Error',
            loading: 'Loading...'
        },
        messages: {
            deleteConfirm: 'Are you sure you want to delete this user?',
            generalError: 'Something went wrong! Please try again',
            addSuccess: 'Record added successfully!',
            updateSuccess: 'Record updated successfully!',
        },
        placeholder: {
            avatar: '?'
        },
        labels: {
            loading: 'Loading',
            noResults: 'No results found',
            noData: 'No data available',
            selected: 'Selected',
            required: 'Required',
            optional: 'Optional',
            search: 'Search...',
            filter: 'Filter...',
            from: 'From',
            to: 'To',
            date: 'Date',
            status: 'Status',
            item: 'Item {index}',
            addItem: 'Add Item',
            removeItem: 'Remove Item',
            skipToMain: 'Skip to main content',
            openNavigation: 'Open navigation',
            closeNavigation: 'Close navigation',
            mainNavigation: 'Main navigation',
            moveUp: 'Move Up',
            moveDown: 'Move Down',
            and: 'and'
        },
        errors: {
            upload: 'Failed to upload file',
            download: 'Failed to download file',
            generic: 'An error occurred',
            notFound: 'Not found',
            unauthorized: 'Unauthorized access',
            validation: 'Please check your input'
        },
        validation: {
            required: 'This field is required',
            email: 'Invalid email address',
            minLength: 'Minimum length is {length}',
            maxLength: 'Maximum length is {length}',
            min: 'Minimum value is {min}',
            max: 'Maximum value is {max}',
            matches: 'Invalid format',
            unique: 'Value must be unique',
            confirm: 'Passwords do not match'
        }
    },
    pages: {
        users: {
            title: 'Users',
            description: 'Manage system users',
            actions: {
                invite: 'Invite User',
                resetPassword: 'Reset Password'
            }
        },
        settings: {
            title: 'Settings',
            actions: {
                resetDefaults: 'Reset to Defaults'
            }
        },
        reports: {
            title: 'Reports',
            description: 'Generate and download reports',
            actions: {
                generateReport: 'Generate Report',
                downloadCsv: 'Download CSV',
                startDate: 'Start Date',
                endDate: 'End Date',
                apps: 'Apps',
            },
            error: {
                generation: 'Failed to generate report',
                download: 'Failed to download report'
            }
        },
        continueListening: {
            titleListen: 'Keep listening',
            titleRead: 'Keep reading',
            titleWatch: 'Keep watching',
            subscribeMessageListen: 'You have reached the end of this preview. Subscribe to continue listening to the full title.',
            subscribeMessageRead: 'You have reached the end of this preview. Subscribe to continue reading the full title.',
            subscribeMessageWatch: 'You have reached the end of this preview. Subscribe to continue watching the full title.',
            purchaseMessageListen: 'You have reached the end of this preview. Purchase this title to continue listening.',
            purchaseMessageRead: 'You have reached the end of this preview. Purchase this title to continue reading.',
            purchaseMessageWatch: 'You have reached the end of this preview. Purchase this title to continue watching.',
            backToTitle: 'Back to title'
        }
    },
    navigation: {
        home: 'Home',
        library: 'Library',
        search: 'Search',
        login: 'Login',
        signInToGetPremium: 'Get Premium',
        subscribe: 'Subscribe',
        support: 'Support',
        settings: 'Settings',
        logout: 'Logout'
    },
    settings: {
        title: 'Settings',
        description: 'Manage your account settings, preferences, and profile information.',
        sections: 'Settings sections',
        menu: {
            profile: 'Profile',
            account: 'Account',
            general: 'General',
            purchases: 'Purchases',
            activity: 'Activity'
        },
        profile: {
            title: 'Profile',
            changeImage: 'Change Image',
            updateProfile: 'Update Profile',
            changePassword: 'Change Password',
            updatePassword: 'Update Password',
            fields: {
                fullName: 'Full Name',
                emailAddress: 'Email Address',
                dateOfBirth: 'Date of Birth',
                currentPassword: 'Current Password',
                newPassword: 'New Password',
                confirmNewPassword: 'Confirm New Password'
            },
            toasts: {
                imageUpdated: 'Image Updated',
                imageUpdatedMessage: 'Your profile image has been updated successfully',
                uploadFailed: 'Upload Failed',
                profileUpdated: 'Profile Updated',
                profileUpdatedMessage: 'Your profile has been updated successfully',
                passwordUpdated: 'Password Updated',
                passwordUpdatedMessage: 'Your password has been updated successfully',
                passwordError: 'Password Error',
                passwordMismatch: 'Passwords do not match',
                passwordTooWeak: 'Password is too weak'
            }
        },
        account: {
            title: 'Account',
            accountInformation: 'Account Information',
            emailAddress: 'Email Address',
            memberSince: 'Member Since',
            redeemPromoCode: 'Redeem promotion code',
            redeemPromoCodeDescription: 'Have a promotion or gift code? Enter it below to apply it to your account or next subscription.',
            subscription: {
                title: 'Subscription',
                currentPlan: 'Current Plan',
                premiumSubscription: 'Premium',
                nextRenewal: 'Next renewal:',
                accessUntil: 'Access until:',
                manageInAppStore: 'Your subscription was purchased through the App Store. To cancel or change it, open Subscriptions in your Apple ID settings on the device you used to subscribe.',
                manageInPlayStore: 'Your subscription was purchased through Google Play. To cancel or change it, open Subscriptions in the Google Play Store on the device you used to subscribe.',
                notSubscribed:
                    'You are not subscribed to a plan. You can start or renew a subscription anytime from here.',
                subscribeNow: 'Subscribe Now',
                findOutMore: 'Find out more',
                cancelSubscription: 'Cancel Subscription',
                cancelSubscriptionTitle: 'Cancel Subscription',
                cancelSubscriptionMessage: 'Are you sure you want to cancel your subscription? You will lose access to premium content at the end of your current billing period.',
                manageSubscription: 'Manage Subscription',
                keepSubscription: 'Keep Subscription',
                toasts: {
                    subscriptionActivated: 'Subscription Activated',
                    subscriptionActivatedMessage: 'Welcome to Premium!',
                    subscriptionFailed: 'Subscription Failed',
                    paymentMethodUpdated: 'Payment Method Updated',
                    paymentMethodUpdatedMessage: 'Your payment information has been updated successfully',
                    subscriptionCancelled: 'Subscription Cancelled',
                    subscriptionCancelledMessage: 'Your subscription will end at the current billing period',
                    cancellationFailed: 'Cancellation Failed'
                }
            },
            closeAccount: {
                title: 'Close Account',
                requestDescription: 'Request to permanently close your account. This action cannot be undone.',
                requestButton: 'Request Account Closure',
                confirmationTitle: 'Are you sure you want to deactivate your account? This will:',
                confirmationItems: {
                    cancelSubscription: 'Cancel your active subscription (if any)',
                    removeCommunications: 'Remove you from all communications',
                    scheduleDeletion: 'Schedule your data to be permanently deleted after {days} days'
                },
                confirmationWarning: 'You can cancel your deactivation request anytime within {days} days. After this period, your account and all associated data will be permanently deleted.',
                confirmationCheckbox: 'I understand that my account will be permanently deleted after {days} days',
                deactivateAccount: 'Deactivate Account',
                cancel: 'Cancel',
                scheduledTitle: 'Account Deactivation Scheduled',
                scheduledMessage: 'Your account is scheduled to be permanently deleted on',
                scheduledWarning: 'You can still log in during this period to cancel the deactivation. After the scheduled date, your account and data will be permanently deleted.',
                deletedTitle: 'Account Closed',
                deletedMessage: 'Your account has been permanently closed and your personal data has been removed.',
                cancelDeactivation: 'Cancel Account Deactivation',
                toasts: {
                    deactivationRequested: 'Account Deactivation Requested',
                    deactivationRequestedMessage: 'Your account will be permanently deleted in {days} days',
                    deactivationFailed: 'Deactivation Failed',
                    deactivationCancelled: 'Deactivation Cancelled',
                    deactivationCancelledMessage: 'Your account is no longer scheduled for deletion'
                }
            }
        },
        purchases: {
            title: 'Purchase History',
            noPurchases: 'No purchases found',
            invoice: 'Invoice'
        },
        activity: {
            title: 'Activity Timeline',
            dateRange: 'Date Range',
            activityType: 'Activity Type',
            noActivities: 'No activities found',
            resume: 'Resume',
            loadMore: 'Load More',
            filters: {
                last7Days: 'Last 7 days',
                last30Days: 'Last 30 days',
                lastYear: 'Last year',
                allTime: 'All time',
                allActivities: 'All Activities',
                listening: 'Listening',
                purchases: 'Purchases',
                likes: 'Likes',
                bookmarks: 'Bookmarks'
            },
            types: {
                listenedTo: 'Listened to {title}',
                purchased: 'Purchased {title}',
                liked: 'Liked {title}',
                bookmarked: 'Bookmarked {title}'
            }
        },
        appSettings: {
            title: 'App Settings',
            darkMode: 'Dark Mode',
            language: 'Language',
            languageOptions: {
                english: 'English',
                spanish: 'Español',
                french: 'Français',
                german: 'Deutsch',
                italian: 'Italiano',
                portuguese: 'Português',
                dutch: 'Nederlands',
                slovak: 'Slovenčina',
                russian: 'Русский',
                arabic: 'العربية'
            }
        },
        notifications: {
            title: 'Notifications',
            emailNotifications: 'Email Notifications',
            emailNotificationsHint: 'Receive updates via email',
            pushNotifications: 'Push Notifications',
            pushNotificationsHint: 'Receive push notifications',
            updatePreferences: 'Update Preferences'
        },
        toasts: {
            themeUpdated: 'Theme Updated',
            themeUpdatedMessage: 'Your theme preference has been saved',
            languageUpdated: 'Language Updated',
            languageUpdatedMessage: 'Your language preference has been saved',
            preferencesUpdated: 'Preferences Updated',
            preferencesUpdatedMessage: 'Your notification preferences have been saved',
            updateFailed: 'Update Failed',
            updateFailedMessage: 'Failed to update setting',
            updateFailedNotificationMessage: 'Failed to update notification preferences'
        }
    },
    auth: {
        login: {
            title: 'Sign In',
            description: 'Sign in to your Heard account to access your library and premium content',
            signIn: 'Sign In',
            signInToAccount: 'Sign in to your account',
            noAccount: "Don't have an account?",
            signUp: 'Sign up',
            continueAsGuest: 'Continue as guest',
            or: 'Or',
            agreeToTerms: 'By signing in, you agree to our',
            termsOfService: 'Terms of Service',
            privacyPolicy: 'Privacy Policy',
            forgotPassword: 'Forgot your password?'
        },
        loginModal: {
            title: 'Sign In Required',
            signInRequired: 'You need to sign in to access this feature.',
            subscribeOrPurchaseRequired: 'Please register or login to subscribe or purchase this content.',
            createAccount: 'Create an account'
        },
        register: {
            title: 'Sign Up',
            description: 'Sign up for a Heard account to access premium audio content, create a personal library, and more',
            createAccount: 'Create Account',
            createYourAccount: 'Create your account',
            orSignIn: 'Or',
            signIn: 'sign in',
            backToHome: 'Back to home',
            agreeToTerms: 'By creating an account, you agree to our',
            termsOfService: 'Terms of Service',
            privacyPolicy: 'Privacy Policy'
        },
        forms: {
            email: 'Email address',
            password: 'Password',
            confirmPassword: 'Confirm Password',
            fullName: 'Full Name',
            rememberMe: 'Remember me',
            signIn: 'Sign in',
            createAccount: 'Create Account'
        },
        toasts: {
            loginSuccess: 'Login successful',
            loginFailed: 'Login failed',
            registerSuccess: 'Account created successfully',
            registerFailed: 'Registration failed'
        },
        activate: {
            title: 'Connect your TV',
            description: 'Approve a sign-in request from your TV',
            heading: 'Connect your TV',
            subheading: 'Enter the code shown on your TV to sign in.',
            checking: 'Checking…',
            codeLabel: 'Code from your TV',
            codePlaceholder: 'ABCD-1234',
            continue: 'Continue',
            requestFor: 'Sign-in request for {app}',
            unknownDevice: 'Your TV',
            confirmPrompt: 'Do you want to sign in to this device with your account?',
            approve: 'Yes, sign in',
            deny: 'No, decline',
            notFound: "We couldn't find that code. Check the code on your TV and try again.",
            appMismatch: 'This code is for a different app. Make sure you are signed in to the right account.',
            tryAnotherCode: 'Enter a different code',
            backToApp: 'Back to app',
            approvedTitle: 'TV connected',
            approvedMessage: 'Your TV is now signed in. You can return to your TV to continue.',
            deniedTitle: 'Sign-in declined',
            deniedMessage: 'The request was declined. Nothing has been connected.',
            expiredTitle: 'Code expired',
            expiredMessage: 'That code has expired. Get a new code on your TV and try again.',
            errorTitle: 'Something went wrong',
            errorMessage: 'We could not complete the request. Please try again.'
        }
    },
    library: {
        title: 'My Library',
        description: 'Access your recently played, liked, purchased, bookmarked, and finished titles in your personal library.',
        sections: 'Library sections',
        noItems: 'No items in this section',
        sync: 'Sync Library',
        tabs: {
            recent: 'Continue',
            liked: 'Favourites',
            purchased: 'Purchased',
            bookmarked: 'Bookmarked',
            finished: 'Listen Again',
            finishedWatch: 'Watch Again',
            finishedRead: 'Read Again'
        }
    },
    home: {
        title: 'Home',
        error: {
            loadFailed: 'Failed to load content'
        }
    },
    player: {
        chapters: 'Chapters',
        options: 'Options',
        playbackSpeed: 'Playback Speed',
        share: 'Share',
        linkCopied: 'Link Copied',
        linkCopiedMessage: 'Share link copied to clipboard!',
        shareError: 'Share Error',
        shareErrorMessage: 'Could not copy share link. Please try again.',
        bookmarkAdded: 'Success',
        bookmarkAddedMessage: 'Bookmark added!',
        jumpedToBookmark: 'Success',
        jumpedToBookmarkMessage: 'Jumped to bookmark!',
        likeError: 'Like Error',
        likeErrorMessage: 'Unable to update like status. Please try again.',
        bookmarkError: 'Bookmark Error',
        bookmarkErrorMessage: 'Unable to update bookmark. Please try again.',
        favorites: {
            add: 'Add to Favorites',
            remove: 'Remove from Favorites'
        },
        unknownChapter: 'Unknown Chapter',
        unknownTitle: 'Unknown Title',
        unknownAuthor: 'Unknown Author'
    },
    details: {
        starring: 'Starring:',
        author: 'Author',
        narrator: 'Narrator',
        pause: 'Pause',
        purchased: 'Purchased',
        read: 'Read',
        readMagazine: 'Read magazine',
        continueReading: 'Continue Reading',
        continueReadingMagazine: 'Continue reading',
        continuePlaying: 'Continue',
        play: 'Play',
        subscribeToRead: 'Subscribe to read',
        subscribeToReadMagazine: 'Subscribe to read',
        subscribeToListen: 'Subscribe to listen',
        subscribeToWatch: 'Subscribe to watch',
        signInToRead: 'Sign in to read',
        signInToListen: 'Sign in to listen',
        signInToWatch: 'Sign in to watch',
        watch: 'Watch',
        continueWatching: 'Continue watching',
        readWithAds: 'Read with ads',
        playWithAds: 'Play with ads',
        continueWithAds: 'Continue with ads',
        loading: 'Loading...',
        noChapters: 'No chapters available',
        chapterFreeLabel: 'Free',
        listen: 'Listen',
        purchaseSuccess: 'Purchase successful',
        purchaseSuccessMessage: '{title} has been added to your library',
        subscriptionSuccess: 'Subscription successful',
        subscriptionSuccessMessage: 'Welcome to Heard Premium!',
        subscriptionFailed: 'Subscription failed',
        subscriptionErrorMessage: 'There was an error processing your subscription.',
        info: 'Info',
        more: 'More',
        episodes: 'Episodes',
        about: 'About',
        series: 'Series',
        producedBy: 'Produced by',
        recommendations: 'You might also like',
        moreFromCategory: 'More from {name}',
        moreFromAuthor: 'More from {name}',
        moreFromCast: 'More from {name}',
        moreFromSeries: 'More in {name}',
        noRelated: 'No related titles available right now.',
        ageVerification: {
            title: 'Adults only',
            message: 'This content is for adults only. Please confirm you understand and want to continue.',
            minAgeHint: 'You must be at least {age} years old to view this title.',
            confirm: 'I am an adult, continue',
            cancel: 'Cancel',
            missingDobTitle: 'Date of birth required',
            missingDobMessage: 'Add your date of birth in your profile to view this content.',
            underageTitle: 'Age restricted',
            underageMessage: 'You must be at least {age} years old to view this content.',
        },
    },
    shareModal: {
        title: 'Share',
        copyLink: 'Copy link',
        copyLinkWithMessage: 'Copy link with message',
        copyLinkWithMessageHint: 'Title and description included',
        shareVia: 'Share via...',
        shareViaHint: 'Apps, messages, email',
        linkCopied: 'Link copied',
        linkCopiedMessage: 'Share link copied to clipboard!',
        shared: 'Shared',
        sharedMessage: 'Content shared successfully'
    },
    contextMenu: {
        playFromBeginning: 'Play from Beginning',
        markAsFinished: 'Mark as Finished',
        addToFavorites: 'Add to Favorites',
        removeFromFavorites: 'Remove from Favorites',
        share: 'Share',
        viewDetails: 'View Details',
        removeFromLibrary: 'Remove from Library',
        continuePlaying: 'Continue Playing',
        continueReading: 'Continue Reading',
        continueWatching: 'Continue watching'
    },
    actions: {
        subscribe: 'Subscribe',
        signInToGetPremium: 'Get Premium',
        purchase: 'Purchase',
        buy: 'Buy',
        buyFor: 'Buy for {price}',
        purchasedGoToLibrary: 'Purchased - Go to Library',
        addToFavorites: 'Add to favorites',
        removeFromFavorites: 'Remove from favorites',
        share: 'Share'
    },
    browse: {
        sortBy: 'Sort By',
        sortOptions: {
            recent: 'Recent',
            ascending: 'A-Z',
            descending: 'Z-A'
        }
    },
    cookieConsent: {
        title: 'Cookies',
        message: 'We use cookies to enhance your browsing experience and analyse our traffic. By clicking "Accept", you consent to our use of cookies. Learn more in our',
        privacyPolicy: 'Privacy Policy',
        accept: 'Accept',
        reject: 'Reject'
    },

    legal: {
        companyNumber: 'Company No.',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        agreeToTermsSubscribe: 'By subscribing, you agree to our',
        faqs: 'FAQs',
        operatedBy: '{appName} is operated by {companyName}, {address}'
    },
    support: {
        title: 'Support',
        description: 'Get help with your account, browse FAQs, and contact our support team.',
        intro: 'Need help with your account, subscription, or listening experience? Browse our FAQs and legal information below, or contact us if you need further assistance.',
        quickLinks: 'Quick Links',
        contactTitle: 'Contact Support',
        contactDescription: 'Send a message to our support team. Check the FAQs first for quick answers.',
        contactIntro: 'If you need help, we\'re here for you. Many common questions are already answered in our help centre.',
        contactIntroFaqBefore: 'Please see our ',
        contactIntroFaqAfter: ' for answers to common questions. If you still need assistance, fill in the form and we\'ll get back to you as soon as we can.',
        contactSubmit: 'Send Message',
        contactName: 'Name',
        contactEmail: 'Email',
        contactSubject: 'Subject',
        contactMessage: 'Message',
        contactMessageHint: 'Please describe your issue in detail',
        backToSupport: 'Back to Support',
        pageLoading: 'Loading…',
        pageNotAvailable: 'This page is not available at the moment.',
        unsupportedSection: 'Unsupported content section: {name}',
        useContactFormBefore: 'Need to get in touch? Use our ',
        contactSuccessTitle: 'Message sent',
        contactSuccessMessage: 'Your message has been sent. We\'ll get back to you as soon as we can.',
        contactErrorTitle: 'Failed to send',
        contactErrorMessage: 'Could not send your message. Please try again.'
    },
    promo: {
        haveCode: 'Have a promotion code?',
        placeholder: 'Enter code',
        apply: 'Apply',
        remove: 'Remove',
        applied: '{name} – {discount}',
        invalidCode: 'Invalid or expired code.',
        expired: 'This code has expired.',
        notValidForPurchase: 'This code is not valid for this purchase.',
        free: 'Free',
        percentOff: '{value}% off',
        fixedOff: '{value} off',
        useWhenSubscribe: 'Use this offer when you subscribe below.',
        useWhenPurchase: 'Use this code when purchasing a title.',
        accessGranted: 'Access has been applied to your account.'
    },
    finished: {
        thanksForListening: 'Thanks for listening',
        thanksForWatching: 'Thanks for watching',
        thanksForReading: 'Thanks for reading',
        enjoy: 'Did you enjoy this?',
        loved: 'Loved it',
        liked: 'Liked it',
        notForMe: 'Not for me',
        lovedMessage: 'Glad you loved it!',
        likedMessage: 'Thanks for the feedback!',
        notForMeMessage: 'We will improve your recommendations.',
        thanks: 'Thanks!',
        noted: 'Noted',
        returnHome: 'Return to Home',
        myLibrary: 'My Library',
        loadError: 'Failed to load this title.'
    },
    onboarding: {
        skip: 'Skip',
        continue: 'Continue',
        subscribe: 'Subscribe',
        startListening: 'Start listening',
        startWatching: 'Start watching',
        startReading: 'Start reading',
        progress: 'Tour progress',
        step: 'Step {current} of {total}',
        perMonth: '{price} / month',
        slides: {
            heardWelcome: {
                title: 'Welcome',
                message: 'Stories, narrated. Take a short tour of how to find something and keep your place.',
            },
            heardHome: {
                title: 'Home',
                message: 'Browse Home for new stories, or search when you already know what you want.',
            },
            heardLibrary: {
                title: 'Library',
                message: 'Save favourites to your library, and drop a bookmark when you need to stop.',
            },
            heardPace: {
                title: 'Your pace',
                message: 'Speed the narration up or slow it down whenever you like.',
            },
            heardSleep: {
                title: 'Sleep timer',
                message: 'Set a timer so playback fades out and your place is waiting next time.',
            },
            heardSubscribe: {
                title: 'Listen freely',
                message: 'Unlimited listening, exclusive and original audio, and new stories each month. Cancel any time.',
            },
            lamourWelcome: {
                title: 'Welcome',
                message: 'A private place for audio stories. Here is a quick look around.',
            },
            lamourDiscover: {
                title: 'Discover',
                message: 'Browse Home or search for a mood, a voice, or a story.',
            },
            lamourLibrary: {
                title: 'Library',
                message: 'Keep favourites close, and bookmark a moment you want to revisit.',
            },
            lamourPace: {
                title: 'Your pace',
                message: 'Change the narration speed any time you are listening.',
            },
            lamourSleep: {
                title: 'Sleep timer',
                message: 'Let a timer stop playback and save your place for later.',
            },
            lamourSubscribe: {
                title: 'Listen freely',
                message: 'Unlimited listening, original audio, and new stories each month. Cancel any time.',
            },
            bibleWelcome: {
                title: 'Welcome',
                message: 'Daily praise, short reflections, and the narrated King James Bible.',
            },
            bibleToday: {
                title: 'Today',
                message: 'Start with a short inspiration or prayer, ready whenever you are.',
            },
            bibleScripture: {
                title: 'Scripture',
                message: 'Listen through the fully narrated King James Bible at your pace.',
            },
            biblePlace: {
                title: 'Your place',
                message: 'Save a passage to your library, or bookmark the verse you want to return to.',
            },
            bibleRest: {
                title: 'Rest',
                message: 'Set a sleep timer so listening winds down and your place is kept.',
            },
            bibleSubscribe: {
                title: 'Listen freely',
                message: 'Daily praise, the narrated Bible, and a place to return to. Cancel any time.',
            },
            shelfWelcome: {
                title: 'Welcome',
                message: 'Audiobooks, shelved by genre. Here is how to start listening.',
            },
            shelfBrowse: {
                title: 'The shelf',
                message: 'Featured books and new releases sit on Home. Browse by genre when you want to wander.',
            },
            shelfLibrary: {
                title: 'Library',
                message: 'Save books you love, and bookmark the chapter you are in.',
            },
            shelfPace: {
                title: 'Your pace',
                message: 'Speed the reading up on a commute, or slow it down at night.',
            },
            shelfSleep: {
                title: 'Sleep timer',
                message: 'Set a timer so the chapter stops and your bookmark is saved.',
            },
            shelfSubscribe: {
                title: 'Read freely',
                message: 'Unlimited reading, exclusive books, and new titles each month. Cancel any time.',
            },
            seenWelcome: {
                title: 'Welcome',
                message: 'Video, ready to watch. A short tour before you press play.',
            },
            seenBrowse: {
                title: 'Browse',
                message: 'Featured videos and new releases are on Home. Categories are there when you want to look further.',
            },
            seenFavourites: {
                title: 'Favourites',
                message: 'Save anything you want to come back to. It stays in your library.',
            },
            seenContinue: {
                title: 'Keep watching',
                message: 'Leave a video and pick it up again from Continue on Home.',
            },
            seenSubscribe: {
                title: 'Watch freely',
                message: 'Unlimited watching, new videos, and downloads. Cancel any time.',
            },
            mxWelcome: {
                title: 'Welcome',
                message: 'Books, ready to read. Here is a short look around the shelf.',
            },
            mxShelf: {
                title: 'The shelf',
                message: 'Featured books and new releases sit on Home. Browse when you want to wander.',
            },
            mxLibrary: {
                title: 'Library',
                message: 'Save books you love, and bookmark the chapter you are in.',
            },
            mxPace: {
                title: 'Your place',
                message: 'Pick up a chapter again, or slow down when you want to linger.',
            },
            mxSleep: {
                title: 'Bookmarks',
                message: 'Leave a bookmark so the page is waiting the next time you open the book.',
            },
            mxSubscribe: {
                title: 'Read freely',
                message: 'The full shelf, offline reading, and an ad-free library. Cancel any time.',
            },
            motorWelcome: {
                title: 'Welcome',
                message: 'Magazines and posters, ready to read. Here is a short look around.',
            },
            motorBrowse: {
                title: 'Home',
                message: 'Browse new issues and posters, or search when you already know the title.',
            },
            motorLibrary: {
                title: 'Library',
                message: 'Save issues you want to come back to. They stay in your library.',
            },
            motorSubscribe: {
                title: 'Read freely',
                message: 'All PDF magazines and posters, with new articles through the month. Cancel any time.',
            },
        },
    },
}

export default messages
